import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/** Model or rule confidence, 0..1 — mirrors the `app.confidence_score` domain. */
export const confidenceSchema = z.number().min(0).max(1);
/**
 * The extraction contract.
 *
 * This file defines the exact shape the model is required to return, and it is
 * the same object the browser renders. One definition, so the panel cannot
 * drift from the payload.
 *
 * THREE RULES THAT SHAPE EVERYTHING HERE
 *
 * 1. **Every field carries its own confidence.** Not one score per document.
 *    "The vendor is certain but the serial number is a guess" is the single
 *    most useful thing a reviewer can be told, and a document-level average
 *    destroys it.
 *
 * 2. **Money is a string, never a number.** JSON numbers are IEEE doubles, and
 *    the moment `1234.56` round-trips through one, the minor-units discipline
 *    the whole ledger rests on (ADR 0005) is already broken. The model returns
 *    what it read; our code parses it to bigint minor units and records a
 *    validation issue if it cannot.
 *
 * 3. **Unknown is null, never a guess.** Below the confidence floor a value is
 *    discarded. A null field costs a reviewer ten seconds; an invented vendor
 *    name costs them a wrong ledger entry they may never catch.
 *
 * SCHEMA CONSTRAINTS ARE DELIBERATELY ABSENT
 * Structured outputs reject `minimum`, `maximum`, `minLength`, and friends, so
 * this schema states shapes only. Range checks (confidence in 0..1, dates that
 * parse, totals that add up) happen in the validator, where a violation
 * becomes a reviewable issue instead of a hard parse failure.
 */
/** Bump when the payload shape changes. Recorded on every run. */
export const EXTRACTION_SCHEMA_VERSION = 1;
/**
 * A single extracted field: what was read, and how sure the model is.
 *
 * `confidence` is unconstrained here on purpose — see the note above. The
 * validator clamps and flags it.
 */
function extracted(value) {
    return z.object({
        value: value.nullable(),
        confidence: z.number(),
    });
}
const extractedText = () => extracted(z.string());
/** Money as the document printed it: "1,234.56", "$1234.56", "(45.00)". */
const extractedMoney = () => extracted(z.string());
/** A date as printed. Normalisation and plausibility are the validator's job. */
const extractedDate = () => extracted(z.string());
const extractedNumber = () => extracted(z.number());
export const EXTRACTED_DOCUMENT_TYPES = [
    'receipt',
    'invoice',
    'supplier_bill',
    'purchase_order',
    'statement',
    'warranty',
    'credit_memo',
    'payroll_report',
    'unknown',
];
export const extractedDocumentTypeSchema = z.enum(EXTRACTED_DOCUMENT_TYPES);
/**
 * The payload the model must produce.
 *
 * Field names are the reviewer's vocabulary rather than the accountant's:
 * `total`, not `gross_amount_inclusive`. The model reads them too, and plain
 * names extract measurably better than jargon.
 */
export const extractionPayloadSchema = z.object({
    documentType: extracted(extractedDocumentTypeSchema),
    vendor: z.object({
        name: extractedText(),
        address: extractedText(),
        phone: extractedText(),
        email: extractedText(),
        /** The supplier's GST/HST registration. Required to claim an ITC. */
        taxNumber: extractedText(),
    }),
    identifiers: z.object({
        invoiceNumber: extractedText(),
        purchaseOrderNumber: extractedText(),
        receiptNumber: extractedText(),
        referenceNumber: extractedText(),
    }),
    dates: z.object({
        transactionDate: extractedDate(),
        dueDate: extractedDate(),
        paymentDate: extractedDate(),
    }),
    totals: z.object({
        /** ISO 4217 as printed, or null. Absence usually means CAD, but inferring is the reviewer's call. */
        currency: extractedText(),
        subtotal: extractedMoney(),
        taxAmount: extractedMoney(),
        /** As a percentage, e.g. 15 for New Brunswick HST. */
        taxRate: extractedNumber(),
        /**
         * "yes" when the tax is already inside the total rather than added to it.
         * Fuel is the everyday case: the pump price includes HST and the receipt
         * prints its tax lines as $0.00, disclosing the real tax at the bottom.
         * Reading that as zero tax loses the credit on every tank.
         */
        taxIncludedInTotal: extractedText(),
        discount: extractedMoney(),
        shipping: extractedMoney(),
        total: extractedMoney(),
    }),
    payment: z.object({
        method: extractedText(),
    }),
    /**
     * Line items, in document order.
     *
     * Serial and model numbers are arrays because one line can cover several
     * units — three identical condensers with three serials is an ordinary
     * HVAC invoice, and flattening it to one string loses the warranty trail.
     */
    lineItems: z.array(z.object({
        description: extractedText(),
        quantity: extractedNumber(),
        unitCost: extractedMoney(),
        extendedCost: extractedMoney(),
        serialNumbers: extracted(z.array(z.string())),
        modelNumbers: extracted(z.array(z.string())),
        equipmentBrand: extractedText(),
    })),
    /** Anything on the document a reviewer would want but no field captures. */
    notes: extractedText(),
});
/* -------------------------------------------------------------------------- */
/* Stored shapes                                                              */
/* -------------------------------------------------------------------------- */
export const EXTRACTION_STATUSES = ['running', 'succeeded', 'failed'];
export const extractionStatusSchema = z.enum(EXTRACTION_STATUSES);
export const EXTRACTION_FAILURE_KINDS = [
    'provider_error',
    'invalid_json',
    'schema_rejected',
    'refused',
    'timeout',
    'document_unreadable',
];
export const extractionFailureKindSchema = z.enum(EXTRACTION_FAILURE_KINDS);
export const EXTRACTION_ISSUE_SEVERITIES = ['error', 'warning', 'info'];
export const extractionIssueSeveritySchema = z.enum(EXTRACTION_ISSUE_SEVERITIES);
export const EXTRACTION_ISSUE_CODES = [
    'math_subtotal_tax_total_mismatch',
    'math_line_items_do_not_sum',
    'missing_vendor',
    'missing_total',
    'missing_transaction_date',
    'unparsable_amount',
    'unparsable_date',
    'date_in_future',
    'date_implausibly_old',
    'unknown_currency',
    'negative_total',
    'implausible_total',
    'tax_rate_out_of_range',
    'invoice_number_format',
    'duplicate_invoice_number',
    'duplicate_document_content',
    'low_confidence_field',
    'document_type_unknown',
];
export const extractionIssueCodeSchema = z.enum(EXTRACTION_ISSUE_CODES);
export const extractionIssueSchema = z.object({
    id: uuidSchema,
    code: extractionIssueCodeSchema,
    severity: extractionIssueSeveritySchema,
    fieldPath: z.string().nullable(),
    message: z.string(),
    detail: z.record(z.string(), z.unknown()),
});
/** One field as stored: the value, its confidence, and its runners-up. */
export const extractedFieldSchema = z.object({
    fieldPath: z.string(),
    value: z.unknown(),
    confidence: confidenceSchema,
    alternatives: z.array(z.object({ value: z.unknown(), confidence: z.number() })),
});
/**
 * An extraction run, as the viewer shows it.
 *
 * Provenance is not optional decoration: "which prompt version produced this,
 * on which model" is the first question asked when an extraction looks wrong,
 * and the last one anybody can answer if it was not recorded at the time.
 */
export const documentExtractionSchema = z.object({
    id: uuidSchema,
    documentId: uuidSchema,
    documentVersionId: uuidSchema,
    attempt: z.number().int(),
    status: extractionStatusSchema,
    provider: z.string(),
    modelName: z.string(),
    modelServed: z.string().nullable(),
    promptName: z.string(),
    promptVersion: z.number().int(),
    promptHash: z.string(),
    schemaVersion: z.number().int(),
    effort: z.string().nullable(),
    /** Always null. Opus 4.7+ rejects the parameter; `effort` replaced it. */
    temperature: z.number().nullable(),
    confidenceFloor: confidenceSchema,
    documentType: extractedDocumentTypeSchema.nullable(),
    documentTypeConfidence: confidenceSchema.nullable(),
    inputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
    cacheReadTokens: z.number().int().nullable(),
    cacheWriteTokens: z.number().int().nullable(),
    providerLatencyMs: z.number().int().nullable(),
    durationMs: z.number().int().nullable(),
    failureKind: extractionFailureKindSchema.nullable(),
    failureReason: z.string().nullable(),
    errorIssueCount: z.number().int(),
    warningIssueCount: z.number().int(),
    meanConfidence: confidenceSchema.nullable(),
    extractedFieldCount: z.number().int(),
    nullFieldCount: z.number().int(),
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
});
/** A run with everything needed to render it beside the original. */
export const documentExtractionDetailSchema = documentExtractionSchema.extend({
    fields: z.array(extractedFieldSchema),
    issues: z.array(extractionIssueSchema),
});
export const extractionStatsSchema = z.object({
    totalRuns: z.number().int(),
    succeeded: z.number().int(),
    failed: z.number().int(),
    inFlight: z.number().int(),
    retries: z.number().int(),
    avgDurationMs: z.number().nullable(),
    avgProviderLatencyMs: z.number().nullable(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    cacheReadTokens: z.number().int(),
    avgConfidence: z.number().nullable(),
    unknownFieldRate: z.number().nullable(),
    runsWithErrors: z.number().int(),
    runsWithWarnings: z.number().int(),
    /** Awaits Phase 5: no corrections exist until a human can make one. */
    correctionRate: z.number().nullable(),
});
export const requestExtractionSchema = z.object({
    /** Re-extract a document that already has a run, e.g. after a prompt change. */
    force: z.boolean().default(false),
});
/* -------------------------------------------------------------------------- */
/* Presentation                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Field labels, in the reviewer's language.
 *
 * Kept beside the schema rather than in the web app so that adding a field
 * without labelling it is a compile error, not a screen showing `totals.subtotal`
 * to a bookkeeper.
 */
export const FIELD_LABELS = {
    documentType: 'Document type',
    'vendor.name': 'Supplier',
    'vendor.address': 'Address',
    'vendor.phone': 'Phone',
    'vendor.email': 'Email',
    'vendor.taxNumber': 'GST/HST number',
    'identifiers.invoiceNumber': 'Invoice number',
    'identifiers.purchaseOrderNumber': 'PO number',
    'identifiers.receiptNumber': 'Receipt number',
    'identifiers.referenceNumber': 'Reference',
    'dates.transactionDate': 'Date',
    'dates.dueDate': 'Due',
    'dates.paymentDate': 'Paid',
    'totals.currency': 'Currency',
    'totals.subtotal': 'Subtotal',
    'totals.taxAmount': 'Tax',
    'totals.taxRate': 'Tax rate',
    'totals.discount': 'Discount',
    'totals.shipping': 'Shipping',
    'totals.total': 'Total',
    'payment.method': 'Payment method',
    notes: 'Notes',
};
/** Labels for the repeated part of a line item, keyed by leaf name. */
export const LINE_ITEM_LABELS = {
    description: 'Description',
    quantity: 'Qty',
    unitCost: 'Unit cost',
    extendedCost: 'Amount',
    serialNumbers: 'Serial numbers',
    modelNumbers: 'Model numbers',
    equipmentBrand: 'Brand',
};
/** `lineItems[2].unitCost` → "Unit cost (line 3)". */
export function labelForFieldPath(fieldPath) {
    const direct = FIELD_LABELS[fieldPath];
    if (direct !== undefined)
        return direct;
    const lineItem = /^lineItems\[(\d+)\]\.(.+)$/.exec(fieldPath);
    if (lineItem !== null) {
        const [, index, leaf] = lineItem;
        const leafLabel = LINE_ITEM_LABELS[leaf ?? ''] ?? leaf ?? fieldPath;
        return `${leafLabel} (line ${Number(index) + 1})`;
    }
    return fieldPath;
}
export function confidenceBand(confidence) {
    if (confidence >= 0.9)
        return 'high';
    if (confidence >= 0.7)
        return 'medium';
    return 'low';
}
//# sourceMappingURL=extraction.js.map